/// <amd-module name="Artifacts/Package/containers/PackageDetailsTabManager" />
import { OverviewTabContainer } from "Artifacts/Package/containers/OverviewTabContainer";
import { UpstreamVersionsTabContainer } from "Artifacts/Package/containers/UpstreamVersionsTabContainer";
import { VersionsTabContainer } from "Artifacts/Package/containers/VersionsTabContainer";
import { IPackageDetailsModuleState, PackageActions, PackageModule } from "Artifacts/Package/redux";
import * as PackageRes from "Artifacts/Package/Resources";
import * as Res from "Artifacts/Package/Resources";
import { ICommandControlProps } from "Artifacts/Package/types";
import { isPublicFeed } from "Artifacts/Root/helpers/FeedHelpers";
import {
  isUserAnonymousOrPublic,
  isUserPermittedToUseCommand,
} from "Artifacts/Root/helpers/UserHelpers";
import { ITabDefinition, useRouteTabManager } from "Artifacts/Root/hooks/RouteTabManager";
import { ICommand } from "Artifacts/Root/protocols/types";
import { connectDynamicModules } from "Artifacts/Root/redux";
import * as RootRes from "Artifacts/Root/Resources";
import {
  connectToFeed,
  packageDetailsOverview,
  packageDetailsSearchUpstreams,
  packageDetailsVersions,
} from "Artifacts/Root/routing/Constants";
import { IFeed, IPackage } from "Artifacts/Root/types";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";
import { useComponentContext } from "VSS/Platform/Layout";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MenuItemType } from "VSSUI/Menu";
import { Tab, TabBar } from "VSSUI/Tabs";

type IPackageDetailsTabManagerProps = {
  feed: IFeed;
};

const Actions = {
  getFollowState: PackageActions.getFollowState,
  setFollowState: PackageActions.setFollowState,
};

const defaultTab = "overview";

const PackageDetailsTabManager: React.FC<IPackageDetailsTabManagerMapStateToProps &
  typeof Actions &
  IPackageDetailsTabManagerProps> = ({
  feed,
  pkg,
  followState,
  getFollowState,
  setFollowState,
}) => {
  const { pageContext } = useComponentContext();
  const [commands, setCommands] = useState<ICommand[]>([]);
  const history = useHistory();
  const tabs = React.useMemo(() => {
    return getTabDefinitions(feed);
  }, [feed]);
  const [selectedTab, changeTab, CurrentTabComponent] = useRouteTabManager<ICommandControlProps>(
    tabs,
    defaultTab,
  );
  useEffect(() => {
    feed && pkg && getFollowState(feed, pkg);
  }, [feed, pkg, getFollowState]);

  const isUserAnonymous = isUserAnonymousOrPublic(pageContext);
  const commandBarItems: IHeaderCommandBarItem[] = [
    ...commands.map((command) => ({
      id: command.id,
      text: command.displayName,
      onActivate: command.action,
      itemType: MenuItemType.Normal,
      iconProps: command.iconProps,
      disabled:
        (isUserAnonymous && !command.isAnonymousAllowed) ||
        (feed != null && isUserPermittedToUseCommand(command, feed)),
    })),
    {
      id: "connect-to-feed",
      text: RootRes.ConnectToFeedButton,
      iconProps: {
        iconName: "PlugConnected",
      },
      onActivate: () => {
        history.push(generatePath(connectToFeed, { feedIdentifier: feed?.name }));
      },
      itemType: MenuItemType.Normal,
    },
  ];

  pkg &&
    commandBarItems.push({
      id: "Follow",
      text: followState ? PackageRes.Unfollow : PackageRes.Follow,
      onActivate: () => {
        feed && pkg && setFollowState(feed, pkg, !followState);
      },
      itemType: MenuItemType.Normal,
      iconProps: {
        iconName: followState ? "Hide2" : "View",
      },
      disabled: feed && isPublicFeed(feed) && isUserAnonymous,
    });

  return (
    <>
      <CustomHeader>
        <HeaderTitleArea>
          {feed && (
            <TabBar selectedTabId={selectedTab.tabId} onSelectedTabChanged={changeTab}>
              {tabs.map((tab, i) => (
                <Tab className="package-tab" key={i} id={tab.tabId} name={tab.name} />
              ))}
            </TabBar>
          )}
        </HeaderTitleArea>
        {<HeaderCommandBar items={commandBarItems} buttonCount={3} />}
      </CustomHeader>
      {CurrentTabComponent && <CurrentTabComponent setCommands={setCommands} />}
    </>
  );
};

function getTabDefinitions(
  feed: IFeed,
): ITabDefinition<ICommandControlProps>[] {
  const packageDetailsTabs: ITabDefinition<ICommandControlProps>[] = [
    {
      tabId: "overview",
      name: Res.PackageDetails_Overview,
      route: packageDetailsOverview,
      component: OverviewTabContainer,
    },
    {
      tabId: "versions",
      name: Res.PackageDetails_Versions,
      route: packageDetailsVersions,
      component: VersionsTabContainer,
    },
  ];

  !feed.selectedViewId &&
    packageDetailsTabs.push({
      tabId: "upstreamVersions",
      name: Res.PackageDetails_UpstreamVersions,
      route: packageDetailsSearchUpstreams,
      component: UpstreamVersionsTabContainer,
    });

  return packageDetailsTabs;
}

interface IPackageDetailsTabManagerMapStateToProps {
  followState: boolean;
  pkg?: IPackage;
}

const mapStateToProps = (
  state: IPackageDetailsModuleState,
): IPackageDetailsTabManagerMapStateToProps => {
  return {
    followState: state.packageState.ui.followState,
    pkg: state.packageState.data.pkg,
  };
};

const ConnectedPackageDetailsTabManager = connectDynamicModules<IPackageDetailsTabManagerProps>(
  connect<IPackageDetailsTabManagerMapStateToProps, unknown>(
    mapStateToProps,
    Actions,
  )(PackageDetailsTabManager),
  PackageModule,
);

export { ConnectedPackageDetailsTabManager as PackageDetailsTabManager };
